import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarIcon, GlassesIcon, MusicIcon, MapIcon, PhoneIcon, ChevronRightIcon, ImageIcon, BuildingIcon } from 'lucide-react';
import type { Page } from '../App';
interface NavLink {
  label: string;
  page: Page;
}
const desktopNavLinks: NavLink[] = [{
  label: 'EVENTS',
  page: 'events'
}, {
  label: 'GALLERY',
  page: 'gallery'
}, {
  label: 'PACKAGES & TABLES',
  page: 'package-service'
}, {
  label: 'RESERVATIONS',
  page: 'reservation'
}, {
  label: 'CONTACT',
  page: 'contact'
}, {
  label: 'FAQ',
  page: 'faq'
}];
interface MenuCard {
  label: string;
  page: Page;
  icon: React.ReactNode;
  description: string;
  tag: string;
}
const menuCards: MenuCard[] = [{
  label: 'EVENTS',
  page: 'events',
  icon: <MusicIcon size={18} data-id="element-335" />,
  description: 'Upcoming nights, DJs & live performances',
  tag: 'This Week'
}, {
  label: 'GALLERY',
  page: 'gallery',
  icon: <ImageIcon size={18} data-id="element-336" />,
  description: 'Photos & moments from MK Lounge',
  tag: 'New Drops'
}, {
  label: 'PACKAGES & TABLES',
  page: 'package-service',
  icon: <GlassesIcon size={18} data-id="element-337" />,
  description: 'Gold, Platinum & Diamond table packages',
  tag: 'VIP'
}, {
  label: 'RESERVATIONS',
  page: 'reservation',
  icon: <CalendarIcon size={18} data-id="element-338" />,
  description: 'Book your table or VIP experience',
  tag: 'Available Now'
}, {
  label: 'CONTACT',
  page: 'contact',
  icon: <PhoneIcon size={18} data-id="element-339" />,
  description: 'Get in touch with our team',
  tag: 'Open Tonight'
}, {
  label: 'FAQ',
  page: 'faq',
  icon: <MapIcon size={18} data-id="element-340" />,
  description: 'Frequently asked questions',
  tag: 'Info'
}];
export function Navigation({
  currentPage,
  setCurrentPage
}: {
  currentPage: Page;
  setCurrentPage: (p: Page) => void;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  const navigate = (page: Page) => {
    setCurrentPage(page);
    setMenuOpen(false);
  };
  return <>
      <motion.nav initial={{
      y: -20,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled || menuOpen ? 'backdrop-blur-xl bg-black/70 border-b border-[#C9A84C]/20' : 'bg-transparent'}`} data-id="element-341">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between" data-id="element-342">
          {/* Logo */}
          <button onClick={() => navigate('home')} className="font-montserrat font-black text-lg tracking-widest text-[#C9A84C] hover:text-[#E8C97A] transition-colors" data-id="element-343">
            MK LOUNGE
          </button>

          {/* Desktop Nav — font bumped to text-[13px] (+2px from 11px) */}
          <div className="hidden lg:flex items-center gap-7" data-id="element-344">
            {desktopNavLinks.map(link => <button key={link.label} onClick={() => navigate(link.page)} className={`font-montserrat font-600 text-[13px] tracking-widest transition-colors duration-300 relative group ${currentPage === link.page ? 'text-[#C9A84C]' : 'text-white/80 hover:text-[#C9A84C]'}`} data-id="element-345">
                {link.label}
                <span className={`absolute -bottom-1 left-0 h-px bg-[#C9A84C] transition-all duration-300 ${currentPage === link.page ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-346" />
              </button>)}
          </div>

          {/* Desktop CTA & Menu Icon */}
          <div className="hidden lg:flex items-center gap-6" data-id="element-347">
            <button onClick={() => navigate('reservation')} className="font-montserrat font-700 text-[13px] tracking-widest bg-[#C9A84C] text-[#050505] px-5 py-2.5 rounded hover:bg-[#E8C97A] transition-colors duration-300" data-id="element-348">
              RESERVE NOW
            </button>
            <button onClick={() => setMenuOpen(o => !o)} className="relative w-10 h-10 flex items-center justify-center focus:outline-none hover:opacity-80 transition-opacity" aria-label={menuOpen ? 'Close menu' : 'Open menu'} data-id="element-349">
              <svg aria-hidden="true" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 transition-transform duration-300" style={{
              fill: '#C9A84C',
              transform: menuOpen ? 'rotate(326.25deg)' : 'rotate(135deg)'
            }} data-id="element-350">
                <path d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z" data-id="element-351" />
              </svg>
            </button>
          </div>

          {/* Arrow Hamburger — mobile only */}
          <button onClick={() => setMenuOpen(o => !o)} className="lg:hidden relative w-10 h-10 flex items-center justify-center focus:outline-none" aria-label={menuOpen ? 'Close menu' : 'Open menu'} data-id="element-352">
            <svg aria-hidden="true" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 transition-transform duration-300" style={{
            fill: '#C9A84C',
            transform: menuOpen ? 'rotate(326.25deg)' : 'rotate(135deg)'
          }} data-id="element-353">
              <path d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z" data-id="element-354" />
            </svg>
          </button>
        </div>
      </motion.nav>

      {/* Blur overlay */}
      <AnimatePresence data-id="element-355">
        {menuOpen && <motion.div key="blur-bg" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-40" style={{
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        background: 'rgba(5,5,5,0.78)'
      }} onClick={() => setMenuOpen(false)} data-id="element-356" />}
      </AnimatePresence>

      {/* Desktop Side Menu (Right to Left) */}
      <AnimatePresence data-id="element-357">
        {menuOpen && <motion.div key="desktop-menu" initial={{
        x: '100%',
        opacity: 0.5
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: '100%',
        opacity: 0.5
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="fixed top-0 right-0 bottom-0 w-[400px] z-50 hidden lg:flex flex-col bg-[#0a0a0a] border-l border-[#C9A84C]/20 shadow-2xl" data-id="element-358">
            <div className="p-8 flex-1 overflow-y-auto" data-id="element-359">
              <div className="flex items-center justify-between mb-12" data-id="element-360">
                <span className="font-montserrat font-black text-[12px] tracking-[0.4em] text-[#C9A84C]" data-id="element-361">
                  TOP EVENTS
                </span>
                <button onClick={() => setMenuOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors" data-id="element-362">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-363">
                    <path d="M18 6L6 18M6 6l12 12" data-id="element-364" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6" data-id="element-365">
                {/* Event 1 */}
                <div className="group cursor-pointer" onClick={() => navigate('events')} data-id="element-366">
                  <div className="aspect-[16/9] rounded-xl overflow-hidden mb-4 relative" data-id="element-367">
                    <img src="https://cdn.magicpatterns.com/uploads/1HxCUa6riEEckBjunctso5/Friday_NIGHT.webp" alt="Friday Nights" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-368" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" data-id="element-369" />
                    <div className="absolute bottom-3 left-3" data-id="element-370">
                      <span className="bg-[#C9A84C] text-black font-montserrat font-bold text-[10px] tracking-widest px-2 py-1 rounded" data-id="element-371">
                        EVERY FRIDAY
                      </span>
                    </div>
                  </div>
                  <h3 className="font-montserrat font-black text-xl text-white group-hover:text-[#C9A84C] transition-colors mb-1" data-id="element-372">
                    FRIDAY NIGHTS
                  </h3>
                  <p className="font-inter text-sm text-white/50" data-id="element-373">
                    4-8PM Free Buffet • Cocktails • Hookah
                  </p>
                </div>

                {/* Event 2 */}
                <div className="group cursor-pointer" onClick={() => navigate('events')} data-id="element-374">
                  <div className="aspect-[16/9] rounded-xl overflow-hidden mb-4 relative" data-id="element-375">
                    <img src="https://cdn.magicpatterns.com/uploads/fnP18MYqsMc6eEJasSpkrM/MK_LOUNGE.png" alt="R&B Mondays" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-376" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" data-id="element-377" />
                    <div className="absolute bottom-3 left-3" data-id="element-378">
                      <span className="bg-[#C9A84C] text-black font-montserrat font-bold text-[10px] tracking-widest px-2 py-1 rounded" data-id="element-379">
                        EVERY MONDAY
                      </span>
                    </div>
                  </div>
                  <h3 className="font-montserrat font-black text-xl text-white group-hover:text-[#C9A84C] transition-colors mb-1" data-id="element-380">
                    R&B MONDAYS
                  </h3>
                  <p className="font-inter text-sm text-white/50" data-id="element-381">
                    Happy Hour 4PM-8PM • Drinks, Food, Hookah
                  </p>
                </div>

                {/* Event 3 */}
                <div className="group cursor-pointer" onClick={() => navigate('events')} data-id="element-382">
                  <div className="aspect-[16/9] rounded-xl overflow-hidden mb-4 relative" data-id="element-383">
                    <img src="https://cdn.magicpatterns.com/uploads/pwVKT3ydjMFRMAJ8WUZEdP/DC_NIGHT_LIFE.png" alt="Tequila & Tacos Tuesday" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-384" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" data-id="element-385" />
                    <div className="absolute bottom-3 left-3" data-id="element-386">
                      <span className="bg-[#C9A84C] text-black font-montserrat font-bold text-[10px] tracking-widest px-2 py-1 rounded" data-id="element-387">
                        EVERY TUESDAY
                      </span>
                    </div>
                  </div>
                  <h3 className="font-montserrat font-black text-xl text-white group-hover:text-[#C9A84C] transition-colors mb-1" data-id="element-388">
                    TEQUILA & TACOS
                  </h3>
                  <p className="font-inter text-sm text-white/50" data-id="element-389">
                    $2 Tacos Every Tuesday • Happy Hour 4PM-8PM
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/5" data-id="element-390">
              <button onClick={() => navigate('events')} className="w-full font-montserrat font-bold text-[13px] tracking-widest text-white border border-white/20 py-4 rounded-xl hover:bg-white/5 transition-colors" data-id="element-391">
                VIEW ALL EVENTS
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Mini card menu (Mobile) */}
      <AnimatePresence data-id="element-392">
        {menuOpen && <motion.div key="menu-panel" initial={{
        opacity: 0,
        y: -16
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -16
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 34
      }} className="fixed top-[64px] left-0 right-0 bottom-0 z-50 lg:hidden px-4 pt-2 pb-6 overflow-y-auto" data-id="element-393">
            {/* Brand strip */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="text-center mb-4" data-id="element-394">
              <span className="font-montserrat font-black text-[10px] tracking-[0.4em] text-[#C9A84C]/50" data-id="element-395">
                MK LOUNGE DC
              </span>
            </motion.div>

            {/* Top Events (Mobile) */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15,
          duration: 0.4
        }} className="mb-6" data-id="element-396">
              <div className="flex items-center justify-between mb-3" data-id="element-397">
                <span className="font-montserrat font-black text-[10px] tracking-[0.4em] text-[#C9A84C]" data-id="element-398">
                  TOP EVENTS
                </span>
              </div>
              <div className="flex overflow-x-auto pb-4 -mx-4 px-4 gap-3 snap-x snap-mandatory hide-scrollbar" data-id="element-399">
                {/* Event 1 */}
                <div className="snap-center shrink-0 w-[240px] group cursor-pointer" onClick={() => navigate('events')} data-id="element-400">
                  <div className="aspect-[16/9] rounded-xl overflow-hidden mb-2 relative" data-id="element-401">
                    <img src="https://cdn.magicpatterns.com/uploads/1HxCUa6riEEckBjunctso5/Friday_NIGHT.webp" alt="Friday Nights" className="w-full h-full object-cover" data-id="element-402" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" data-id="element-403" />
                    <div className="absolute bottom-2 left-2" data-id="element-404">
                      <span className="bg-[#C9A84C] text-black font-montserrat font-bold text-[8px] tracking-widest px-1.5 py-0.5 rounded" data-id="element-405">
                        EVERY FRIDAY
                      </span>
                    </div>
                  </div>
                  <h3 className="font-montserrat font-black text-sm text-white mb-0.5" data-id="element-406">
                    FRIDAY NIGHTS
                  </h3>
                  <p className="font-inter text-[10px] text-white/50 truncate" data-id="element-407">
                    4-8PM Free Buffet • Cocktails • Hookah
                  </p>
                </div>

                {/* Event 2 */}
                <div className="snap-center shrink-0 w-[240px] group cursor-pointer" onClick={() => navigate('events')} data-id="element-408">
                  <div className="aspect-[16/9] rounded-xl overflow-hidden mb-2 relative" data-id="element-409">
                    <img src="https://cdn.magicpatterns.com/uploads/fnP18MYqsMc6eEJasSpkrM/MK_LOUNGE.png" alt="R&B Mondays" className="w-full h-full object-cover" data-id="element-410" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" data-id="element-411" />
                    <div className="absolute bottom-2 left-2" data-id="element-412">
                      <span className="bg-[#C9A84C] text-black font-montserrat font-bold text-[8px] tracking-widest px-1.5 py-0.5 rounded" data-id="element-413">
                        EVERY MONDAY
                      </span>
                    </div>
                  </div>
                  <h3 className="font-montserrat font-black text-sm text-white mb-0.5" data-id="element-414">
                    R&B MONDAYS
                  </h3>
                  <p className="font-inter text-[10px] text-white/50 truncate" data-id="element-415">
                    Happy Hour 4PM-8PM • Drinks, Food, Hookah
                  </p>
                </div>

                {/* Event 3 */}
                <div className="snap-center shrink-0 w-[240px] group cursor-pointer" onClick={() => navigate('events')} data-id="element-416">
                  <div className="aspect-[16/9] rounded-xl overflow-hidden mb-2 relative" data-id="element-417">
                    <img src="https://cdn.magicpatterns.com/uploads/pwVKT3ydjMFRMAJ8WUZEdP/DC_NIGHT_LIFE.png" alt="Tequila & Tacos Tuesday" className="w-full h-full object-cover" data-id="element-418" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" data-id="element-419" />
                    <div className="absolute bottom-2 left-2" data-id="element-420">
                      <span className="bg-[#C9A84C] text-black font-montserrat font-bold text-[8px] tracking-widest px-1.5 py-0.5 rounded" data-id="element-421">
                        EVERY TUESDAY
                      </span>
                    </div>
                  </div>
                  <h3 className="font-montserrat font-black text-sm text-white mb-0.5" data-id="element-422">
                    TEQUILA & TACOS
                  </h3>
                  <p className="font-inter text-[10px] text-white/50 truncate" data-id="element-423">
                    $2 Tacos Every Tuesday • Happy Hour 4PM-8PM
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Cards grid — label bumped to text-[16px], description to text-[14px] */}
            <div className="grid grid-cols-1 gap-2.5" data-id="element-424">
              {menuCards.map((card, i) => <motion.button key={card.label} onClick={() => navigate(card.page)} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 10
          }} transition={{
            delay: 0.06 + i * 0.055,
            duration: 0.3,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex items-center gap-4 px-4 py-3.5 rounded-2xl group cursor-pointer w-full text-left" style={{
            background: `rgba(${14 + i * 3}, ${11 + i * 2}, ${8 + i * 2}, 0.96)`,
            border: currentPage === card.page ? '1px solid rgba(201,168,76,0.4)' : '1px solid rgba(42,38,34,0.9)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.5), 0 1px 4px rgba(0,0,0,0.3)'
          }} whileTap={{
            scale: 0.98
          }} data-id="element-425">
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-[#C9A84C] group-hover:bg-[#C9A84C] group-hover:text-[#050505] transition-all duration-300" style={{
              background: currentPage === card.page ? 'rgba(201,168,76,0.2)' : 'rgba(201,168,76,0.1)',
              border: '1px solid rgba(201,168,76,0.2)'
            }} data-id="element-426">
                    {card.icon}
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0" data-id="element-427">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-428">
                      <span className={`font-montserrat font-black text-[16px] tracking-widest group-hover:text-[#C9A84C] transition-colors duration-300 ${currentPage === card.page ? 'text-[#C9A84C]' : 'text-white'}`} data-id="element-429">
                        {card.label}
                      </span>
                      <span className="font-montserrat text-[8px] tracking-widest px-1.5 py-0.5 rounded text-[#050505]" style={{
                  background: '#C9A84C'
                }} data-id="element-430">
                        {card.tag}
                      </span>
                    </div>
                    <p className="font-inter font-light text-[14px] text-white/45 truncate" data-id="element-431">
                      {card.description}
                    </p>
                  </div>

                  {/* Arrow */}
                  <ChevronRightIcon size={14} className="flex-shrink-0 text-[#C9A84C]/30 group-hover:text-[#C9A84C] group-hover:translate-x-0.5 transition-all duration-300" data-id="element-432" />
                </motion.button>)}
            </div>

            {/* Reserve CTA */}
            <motion.button onClick={() => navigate('reservation')} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          delay: 0.38,
          duration: 0.3
        }} className="block mt-3 w-full text-center font-montserrat font-black text-[14px] tracking-widest bg-[#C9A84C] text-[#050505] py-4 rounded-2xl hover:bg-[#E8C97A] transition-colors duration-300" style={{
          boxShadow: '0 4px 20px rgba(201,168,76,0.3)'
        }} data-id="element-433">
              RESERVE NOW
            </motion.button>
          </motion.div>}
      </AnimatePresence>
    </>;
}